/**
 * Invite-screen flow logic (onboarding redesign Task 5) — extracted as pure
 * helpers so the invited-member accept/decline behavior is unit-testable
 * WITHOUT rendering the native screen (same pure-helper style as
 * `src/lib/interview/voice-button-state.ts` and the two component tests under
 * `src/components/interview/__tests__/`).
 *
 * The load-bearing invariant this file guards: the invite is accepted ONLY on
 * an explicit user action, NEVER as a side effect of loading the page. In the
 * phase model below, `accepting`/`accepted`/`accept-error` are reachable solely
 * by feeding a non-idle `acceptState` — which the screen only ever produces
 * from the Accept button's mutation. A freshly mounted screen with a loaded
 * preview and `acceptState: 'idle'` resolves to `preview` (Accept | Decline),
 * so mount can never accept. See `__tests__/invite-flow.test.ts`.
 */

/** Coarse async status for a query/mutation, mapped from React Query flags. */
export type AsyncState = 'idle' | 'loading' | 'success' | 'error';

/** Which screen a given set of inputs should render. */
export type InvitePhase =
  | 'checking-session'
  | 'missing-token'
  | 'declined'
  | 'accepted'
  | 'accept-error'
  | 'accepting'
  | 'loading-preview'
  | 'preview-error'
  | 'preview';

export interface InvitePhaseInput {
  /** `!authLoading` — the auth session has finished resolving. */
  authResolved: boolean;
  /** A real session exists (or mock mode). */
  hasAuth: boolean;
  /** A non-empty invite token is present in the deep link. */
  hasToken: boolean;
  /** The user explicitly declined the invitation. */
  declined: boolean;
  /** State of the ACCEPT mutation — only ever leaves `idle` via the Accept press. */
  acceptState: AsyncState;
  /** State of the read-only PREVIEW query. */
  previewState: AsyncState;
  /** The preview query has resolved data to render. */
  previewHasData: boolean;
}

/**
 * Pure phase resolver for the invite screen. Order matters:
 *   session → token → declined → accept-outcome → accept-inflight →
 *   preview-loading → preview-error → preview.
 *
 * Accept outcomes/inflight are checked BEFORE the preview render so that once
 * the user presses Accept, the screen leaves the preview even though the
 * preview data is still cached. Crucially, with `acceptState: 'idle'` (the
 * mount/default), none of the accept branches fire — the screen rests on
 * `preview`, offering Accept/Decline, and no acceptance has occurred.
 */
export function resolveInvitePhase(i: InvitePhaseInput): InvitePhase {
  if (!i.authResolved || !i.hasAuth) return 'checking-session';
  if (!i.hasToken) return 'missing-token';
  if (i.declined) return 'declined';

  // Accept is reachable ONLY through an explicit user action, so these phases
  // never appear on mount (acceptState defaults to 'idle').
  if (i.acceptState === 'success') return 'accepted';
  if (i.acceptState === 'error') return 'accept-error';
  if (i.acceptState === 'loading') return 'accepting';

  if (i.previewState === 'idle' || i.previewState === 'loading') return 'loading-preview';
  if (i.previewState === 'error' || !i.previewHasData) return 'preview-error';
  return 'preview';
}

/**
 * Whether the Accept action may fire. Gated on the invited email matching the
 * signed-in user (server enforces this on accept too — this only avoids a
 * guaranteed-to-fail round-trip) and on no accept already being in flight.
 */
export function canAcceptInvite(opts: { emailMatches: boolean; accepting: boolean }): boolean {
  return opts.emailMatches && !opts.accepting;
}
